
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/lib/types';

/**
 * Utility function to get the currently authenticated user's company ID
 * This is used for associating templates with companies
 */
export const getUserCompanyId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();
    
    return profile?.company_id || null;
  } catch (error) {
    console.error('Error getting user company ID:', error);
    return null;
  }
};

/**
 * Formats web search data into a readable string format
 * Used for template generation
 */
export const formatWebSearchData = (webSearchData: any): string => {
  if (!webSearchData) return 'No web search data available';
  
  let result = '';
  
  if (webSearchData.summary) {
    result += `- Summary: ${webSearchData.summary}\n`;
  }
  
  if (webSearchData.keyPeople && Array.isArray(webSearchData.keyPeople)) {
    const people = webSearchData.keyPeople
      .map((person: any) => typeof person === 'string' ? person : (person.name || 'Unnamed'))
      .join(', ');
    result += `- Key People: ${people}\n`;
  }
  
  if (webSearchData.industryInsights && Array.isArray(webSearchData.industryInsights)) {
    result += `- Industry Insights: ${webSearchData.industryInsights.join(', ')}\n`;
  }
  
  if (webSearchData.marketPosition) {
    result += `- Market Position: ${webSearchData.marketPosition}\n`;
  }
  
  if (webSearchData.competitors && Array.isArray(webSearchData.competitors)) {
    result += `- Competitors: ${webSearchData.competitors.join(', ')}\n`;
  }
  
  return result || 'Limited web search data available';
};

/**
 * Formats company data into a readable string format
 * Used for template generation
 */
export const formatCompanyData = (companyData: any): string => {
  if (!companyData) return 'No company data available';
  
  let result = '';
  
  if (companyData.name) {
    result += `- Company Name: ${companyData.name}\n`;
  }
  
  if (companyData.summary) {
    result += `- Summary: ${companyData.summary}\n`;
  }
  
  if (companyData.services && Array.isArray(companyData.services)) {
    result += `- Our Services: ${companyData.services.join(', ')}\n`;
  }
  
  if (companyData.products && Array.isArray(companyData.products)) {
    result += `- Our Products: ${companyData.products.join(', ')}\n`;
  }
  
  return result || 'Limited company data available';
};
